import React from 'react';
import { useSelector } from 'react-redux';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ConnectionKind } from '@mm/state/model/ConnectionModel';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { Tooltip } from '@mm/components';
import { MailchimpAccount } from '@mm/state/model/MailchimpModel';
import { ReactComponent as AccountIcon } from '../../../../../../../../layout/BotBuilderSidebar/UserDropdown/UserDropdownIcons/UserFallback.svg';
import { LinkOutlined, TeamOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';

type Props = {
  isAccountConnected: boolean;
  mailchimpCount?: number;
  accountsCount?: number;
  oauthUrl: string;
  className?: string;
  name?: string;
  accounts: MailchimpAccount[];
};

export const MailchimpHeaderComponent = (props: Props) => {
  const {
    isAccountConnected,
    mailchimpCount,
    accountsCount,
    oauthUrl,
    name,
    className,
    accounts,
  } = props;

  const { setActiveIntegration } = useConnectionsTableActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { create } = useMailchimpConnectionCRUDActions();

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.mailchimpContactsSent;

  const { showSlideoutMenu } = useViewActions();

  const addNewConnection = async () => {
    const connection = await create(activeFbPageId, {
      name: 'Mailchimp Connection',
      description: '',
      kind: ConnectionKind.AUDIENCE,
      connectionKind: 'mailchimp',
    });
    if (connection) {
      showSlideoutMenu('mailchimpIntegrationEditor', {
        widgetId: connection.id,
      });
    }
  };

  const onClickAddNew = () => {
    addNewConnection();
    setActiveIntegration(name);
  };

  const onConnectAccountClick = () => {
    window.open(oauthUrl, '_self');
  };

  return (
    <div className={className}>
      <div className="integrations__header-actions">
        <Help
          link="https://customers.ai/help/l/en/article/1a56f1gfmn-integration-with-mailchimp"
          text="Help Center"
        />
        <Tooltip
          placement="bottom"
          title="Mailchimp Accounts used in connections"
        >
          {!!accountsCount && (
            <div className="integrations__header-status">
              <AccountIcon className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {accountsCount}
              </div>
              Accounts
            </div>
          )}
        </Tooltip>
        {isAccountConnected && (
          <>
            <div className="integrations__header-status">
              <LinkOutlined className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {mailchimpCount}
              </div>
              Connections
            </div>

            {!!leadsSent && (
              <div className="integrations__header-status">
                <TeamOutlined className="integrations__header-status-icon" />
                <div className="integrations__header-status-count">
                  {leadsSent}
                </div>
                Leads sent
              </div>
            )}

            <PrimaryButton
              className="integrations__header-add-button-secondary"
              onClick={e => {
                e?.stopPropagation();
                onConnectAccountClick();
              }}
              color={ButtonColor.secondaryBlue}
            >
              Add an Account
            </PrimaryButton>

            <PrimaryButton
              className="integrations__header-add-button-secondary"
              onClick={e => {
                e?.stopPropagation();
                onClickAddNew();
              }}
              color={ButtonColor.secondaryBlue}
              disabled={!accounts.find(acc => !acc.readOnly)}
            >
              Add a Connection
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
};
